import { motion } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface Slide {
  id: string
  title: string
}

interface NavigationProps {
  currentSlide: number
  totalSlides: number
  slides: Slide[]
  onSlideChange: (index: number) => void
  onNext: () => void
  onPrev: () => void
}

export default function Navigation({
  currentSlide,
  totalSlides,
  slides,
  onSlideChange,
  onNext,
  onPrev,
}: NavigationProps) {
  return (
    <>
      {/* Arrow navigation */}
      <div className="fixed inset-y-0 left-0 flex items-center z-40 pl-4">
        <motion.button
          onClick={onPrev}
          disabled={currentSlide === 0}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className={`glass p-3 rounded-full transition-all duration-300 ${
            currentSlide === 0
              ? 'opacity-30 cursor-not-allowed'
              : 'opacity-70 hover:opacity-100 hover:glow-cyan'
          }`}
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </motion.button>
      </div>

      <div className="fixed inset-y-0 right-0 flex items-center z-40 pr-4">
        <motion.button
          onClick={onNext}
          disabled={currentSlide === totalSlides - 1}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className={`glass p-3 rounded-full transition-all duration-300 ${
            currentSlide === totalSlides - 1
              ? 'opacity-30 cursor-not-allowed'
              : 'opacity-70 hover:opacity-100 hover:glow-cyan'
          }`}
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </motion.button>
      </div>

      {/* Dot navigation */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
        <div className="glass rounded-full px-4 py-3 flex items-center gap-2">
          {slides.map((slide, index) => (
            <motion.button
              key={slide.id}
              onClick={() => onSlideChange(index)}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              className="relative group"
              aria-label={`Go to slide ${index + 1}: ${slide.title}`}
            >
              <div
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'bg-electric-cyan scale-110'
                    : 'bg-white/30 hover:bg-white/50'
                }`}
              />
              {index === currentSlide && (
                <motion.div
                  layoutId="activeSlide"
                  className="absolute inset-0 rounded-full ring-2 ring-electric-cyan/50"
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}

              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                <div className="glass-strong px-3 py-1.5 rounded-lg text-xs font-medium text-white whitespace-nowrap">
                  {slide.title}
                </div>
              </div>
            </motion.button>
          ))}
        </div>

        {/* Slide counter */}
        <div className="text-center mt-2 text-sm text-white/40 font-mono">
          {currentSlide + 1} / {totalSlides}
        </div>
      </div>
    </>
  )
}
